import React, { createContext, useContext, useState, useEffect } from "react";
import axiosInstance, { isAxiosError } from "../config/axios";
import { AxiosError } from "axios";

import { User } from "../types/UserTypes";

interface UserController {
  user: User | null;
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUser: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  inviteUser: (payload: {
    first_name: string;
    last_name: string;
    email: string;
    club: number;
    user_type?: string;
  }) => Promise<User>;
  deleteUser: (userId: number) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Create the context with UserController type
const UserContext = createContext<UserController | undefined>(undefined);

// Provider component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/profile/');
      setUser(response.data);
      setLoading(false);
    } catch (error: unknown) {
      console.error("Error fetching user data:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch user data"
      );
      setLoading(false);
    }
  };

  // Automatically fetch user data when the provider mounts
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUser();
    } else {
      console.log("No token found, user not authenticated");
      setLoading(false);
    }
  }, []);

  const fetchUsers = async () => {

    try {
      const response = await axiosInstance.get('/get-users/');
      setUsers(response.data);
    } catch (error: unknown) {
      console.error("Error fetching users:", error);
    }
  };

    const inviteUser = async ({
      first_name,
      last_name,
      email,
      club,
      user_type = "COACH",
    }: {
      first_name: string;
      last_name: string;
      email: string;
      club: number;
      user_type?: string;
    }): Promise<User> => {
    try {
      const response = await axiosInstance.post('/invite-user/', {
        first_name,
        last_name,
        email,
        club,
        user_type
      });
      await fetchUsers(); // Refresh the users list
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to invite user');
      }
      throw new Error('Failed to invite user');
    }
  };

  const deleteUser = async (userId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`/delete-user/${userId}/`);
      await fetchUsers(); // Refresh the users list
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to delete user');
      }
      throw new Error('Failed to delete user');
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await axiosInstance.post('/change-password/', {
        current_password: currentPassword,
        new_password: newPassword
      });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to change password');
      }
      throw new Error('Failed to change password');
    }
  };

  const userController: UserController = {
    user,
    users,
    loading,
    error,
    fetchUser,
    fetchUsers,
    inviteUser,
    deleteUser,
    changePassword,
  };

  return (
    <UserContext.Provider value={userController}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
